import { useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import {
  Avatar,
  Button,
  type Column,
  EmptyState,
  Kpi,
  PageHead,
  Pill,
  SearchInput,
  Table,
  Tabs,
  type TabItem,
} from "@/components/common";
import { useAuth } from "@/auth/useAuth";
import { useActiveWorkspace } from "@/contexts/WorkspaceContext";
import { useMyWorkspaces } from "@/hooks/useWorkspaces";
import type { Workspace } from "@/types/workspace";
import { formatBRL } from "@/utils/fmt";
import { WorkspaceCreateModal } from "@/components/workspaces/WorkspaceCreateModal";
import { AccessLogsPanel } from "./AccessLogsPanel";
import { AuditLogsPanel } from "./AuditLogsPanel";
import { LgpdPanel } from "./lgpd/LgpdPanel";
import { TesesPanel } from "./teses/TesesPanel";
import "./AdminPage.css";

type Tab = "workspaces" | "teses" | "roles" | "services" | "audit" | "access" | "lgpd";

export function AdminPage() {
  const { user } = useAuth();
  const { activeWorkspaceId } = useActiveWorkspace();
  const navigate = useNavigate();
  const { data: workspaces = [] } = useMyWorkspaces();
  const [tab, setTab] = useState<Tab>("workspaces");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);

  const stats = useMemo(() => {
    const ativos = workspaces.filter((w) => w.active).length;
    const mrr = workspaces.reduce((acc, w) => acc + Number(w.plan?.mrr_base ?? 0), 0);
    const clientes = workspaces.filter((w) => !w.is_platform_tenant).length;
    return { total: workspaces.length, ativos, mrr, clientes };
  }, [workspaces]);

  if (!user?.isPlatformAdmin) return <Navigate to="/" replace />;

  const filtered = workspaces.filter(
    (w) => !search.trim() || w.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  const tabs: TabItem<Tab>[] = [
    { id: "workspaces", label: "Workspaces", count: stats.total },
    { id: "teses", label: "Teses tributárias" },
    { id: "roles", label: "Papéis e permissões" },
    { id: "services", label: "Status de serviços" },
    { id: "audit", label: "Auditoria" },
    { id: "access", label: "Acessos" },
    { id: "lgpd", label: "LGPD" },
  ];

  const columns: Column<Workspace>[] = [
    {
      id: "name",
      header: "Workspace",
      cell: (w) => (
        <div className="emp-table-name">
          <Avatar name={w.name} size="md" />
          <div>
            <div className="razao">{w.name}</div>
            <div className="fantasia">{w.is_platform_tenant ? "Workspace principal" : "Cliente"}</div>
          </div>
        </div>
      ),
    },
    {
      id: "tipo",
      header: "Tipo",
      cell: (w) =>
        w.is_platform_tenant ? <Pill tone="onb">Principal</Pill> : <Pill tone="diag">Cliente</Pill>,
    },
    { id: "plan", header: "Plano", cell: (w) => <span className="td-muted">{w.plan?.name ?? "—"}</span> },
    { id: "mrr", header: "MRR", align: "right", cell: (w) => formatBRL(w.plan?.mrr_base ?? null) },
    {
      id: "status",
      header: "Status",
      cell: (w) => <Pill tone={w.active ? "ativa" : "pausada"}>{w.active ? "Ativo" : "Inativo"}</Pill>,
    },
  ];

  return (
    <>
      <PageHead
        title="Admin"
        subtitle="Área restrita · cadastros e configurações que afetam toda a plataforma"
        actions={
          <Button variant="ghost" iconLeft="credit-card" onClick={() => navigate("/admin/plans")}>
            Planos
          </Button>
        }
      />

      <div className="kpi-grid">
        <Kpi icon="building" tone="featured" label="Workspaces" value={String(stats.total)} sub={`${stats.ativos} ativos`} />
        <Kpi icon="briefcase" label="Clientes" value={String(stats.clientes)} sub="workspaces de cliente" />
        <Kpi icon="shield-check" label="Ativos" value={String(stats.ativos)} sub={`${stats.total - stats.ativos} inativos`} />
        <Kpi icon="credit-card" label="MRR total" value={formatBRL(stats.mrr)} sub="somatório dos planos" />
      </div>

      <Tabs items={tabs} active={tab} onChange={setTab} />

      {tab === "workspaces" ? (
        <>
          <div className="emp-toolbar">
            <SearchInput value={search} onChange={setSearch} placeholder="Buscar workspace…" />
            <span className="emp-toolbar-counter">
              {filtered.length} de {stats.total}
            </span>
            <div className="emp-toolbar-spacer" />
            <Button variant="primary" iconLeft="plus" onClick={() => setCreating(true)}>
              Criar workspace
            </Button>
          </div>
          <Table
            rows={filtered}
            columns={columns}
            getKey={(w) => String(w.id)}
            onRowClick={(w) => navigate(`/workspaces/${w.id}`)}
            empty={<EmptyState icon="building" title="Nenhum workspace" />}
          />
        </>
      ) : tab === "teses" ? (
        <TesesPanel />
      ) : tab === "audit" ? (
        activeWorkspaceId != null ? (
          <AuditLogsPanel workspaceId={activeWorkspaceId} />
        ) : (
          <EmptyState
            icon="shield-check"
            size="lg"
            title="Selecione um workspace"
            description="Escolha um workspace ativo para visualizar a trilha de auditoria."
          />
        )
      ) : tab === "access" ? (
        <AccessLogsPanel />
      ) : tab === "lgpd" ? (
        <LgpdPanel />
      ) : (
        <EmptyState
          icon={tab === "services" ? "activity" : "shield-check"}
          size="lg"
          title="Em breve"
          description="Este módulo será habilitado quando o backend correspondente for entregue."
        />
      )}

      {creating && activeWorkspaceId != null && (
        <WorkspaceCreateModal workspaceId={activeWorkspaceId} onClose={() => setCreating(false)} />
      )}
    </>
  );
}
